import { Spinner } from '@alkem/react-ui-spinner';
import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Select, {
  ActionMeta,
  GroupBase,
  OptionProps,
  Props as SelectProps,
} from 'react-select';
import AsyncSelect, { AsyncProps } from 'react-select/async';
import './index.scss';

interface DefaultOptionType {
  id: string | number;
  label?: string;
}

type Optional<T> = { [P in keyof T]?: T[P] };

type ReactSelectProps<
  OptionType,
  IsMultiType extends boolean = false,
  Group extends GroupBase<OptionType> = GroupBase<OptionType>,
> = SelectProps<OptionType, IsMultiType, Group> &
  Optional<AsyncProps<OptionType, IsMultiType, Group>>;

export type TurboSelectActionMeta<OptionType> = ActionMeta<OptionType> & {
  option: OptionType;
  removedValue: OptionType;
};

export type TurboSelectOption<OptionType> =
  | OptionType
  | OptionType[]
  | null
  | undefined;

export interface TurboSelectProps<
  OptionType,
  IsMultiType extends boolean = false,
  Group extends GroupBase<OptionType> = GroupBase<OptionType>,
> extends Omit<ReactSelectProps<OptionType, IsMultiType, Group>, 'onChange'> {
  getOptionChildren?: (opt: OptionType) => OptionType[] | null | undefined;
  isTree?: boolean;
  withBottomBorder?: boolean;
  onChange?: (
    opt: TurboSelectOption<OptionType>,
    action: TurboSelectActionMeta<OptionType>
  ) => void;
}

interface InternalOption {
  _level?: number;
}

interface ExpandedDict {
  [key: string]: boolean;
}

function TurboSelectClearIndicator(props: { innerProps: any }) {
  return (
    <div
      {...props.innerProps}
      className="TurboSelect__clearIndicator"
      data-testid="turbo-select-clear-indicator"
    >
      <i className="mdi mdi-close" />
    </div>
  );
}

function TurboSelectDropdownIndicator() {
  return (
    <div className="TurboSelect__dropdownIndicator">
      <i className="mdi mdi-chevron-down" />
    </div>
  );
}

function TurboSelectLoadingIndicator() {
  return <Spinner small />;
}

function TurboSelectOption<
  OptionType = DefaultOptionType,
  IsMultiType extends boolean = false,
>({
  data,
  innerProps,
  innerRef,
  isFocused,
  isSelected,
  selectProps,
}: OptionProps<OptionType, IsMultiType> & {
  selectProps: {
    isTree?: boolean;
    expandedDict: any;
    getOptionChildren: Function;
  };
}) {
  const {
    getOptionValue = (data) => data,
    getOptionLabel = (data) => data,
    isTree,
    expandedDict,
    getOptionChildren,
  } = selectProps;
  const value = getOptionValue(data);
  const label = getOptionLabel(data);
  let hasChildren = false;
  let isExpanded = false;
  if (isTree && expandedDict && getOptionChildren) {
    const children = getOptionChildren(data);
    hasChildren = !!(children && children.length);
    isExpanded = !!expandedDict[`${getOptionValue(data)}`];
  }
  return (
    <div
      ref={innerRef}
      {...innerProps}
      className={classNames(
        'TurboSelect__option',
        isTree && 'TurboSelect__option--is-tree',
        isSelected && 'TurboSelect__option--is-selected',
        isFocused && 'TurboSelect__option--is-focused'
      )}
      data-value={value}
      style={
        isTree ? { paddingLeft: `${((data as any)._level || 0) + 1}rem` } : {}
      }
    >
      {isTree && (
        <i
          className={classNames(
            'TurboSelect__optionChevron mdi',
            hasChildren && 'TurboSelect__optionChevron--visible',
            isExpanded ? 'mdi-chevron-down' : 'mdi-chevron-right'
          )}
        />
      )}
      <span>{label}</span>
    </div>
  );
}

const defaultProps = {
  getOptionChildren: (opt: any) => opt && opt.children,
  getOptionLabel: (opt: any) => opt && opt.label,
  getOptionValue: (opt: any) => opt && opt.id,
  loadingMessage: () => 'Loading...',
  noOptionsMessage: () => 'No available option',
};

export function TurboSelect<
  OptionType = DefaultOptionType,
  IsMultiType extends boolean = false,
  Group extends GroupBase<OptionType> = GroupBase<OptionType>,
>({
  className,
  components,
  getOptionChildren = defaultProps.getOptionChildren,
  getOptionLabel = defaultProps.getOptionLabel,
  getOptionValue = defaultProps.getOptionValue,
  isClearable = true,
  isSearchable = false,
  isTree = false,
  loadingMessage = defaultProps.loadingMessage,
  loadOptions,
  menuPlacement = 'auto',
  noOptionsMessage = defaultProps.noOptionsMessage,
  onChange,
  openMenuOnClick = true,
  openMenuOnFocus = true,
  options,
  placeholder = '',
  value = null,
  withBottomBorder = false,
  ...otherProps
}: TurboSelectProps<OptionType, IsMultiType, Group>) {
  const selectEl = useRef(null);
  const [internalOptions, updateOptions] = useState<typeof options>();
  const [expandedDict, updateExpandedDict] = useState<ExpandedDict>({});

  useEffect(() => {
    updateOptions(options);
  }, [options]);

  // custom logic for options tree since react-select does not handle it
  const onChangeForTree = useCallback(
    (selection: any, action: any) => {
      const lastSelected = Array.isArray(selection)
        ? selection[selection.length - 1]
        : selection;
      const children = lastSelected && getOptionChildren(lastSelected);
      const localOptions = internalOptions || [];

      if (children && children.length) {
        const index = localOptions.indexOf(lastSelected);
        let newOptions: OptionType[];
        const optionValue = getOptionValue(lastSelected);

        if (expandedDict[`${optionValue}`]) {
          newOptions = [
            ...localOptions.slice(0, index),
            lastSelected,
            ...localOptions.slice(index + 1, localOptions.length),
          ];

          const getChildrenToRemove = (parentOption: any) => {
            let childrenToRemove = getOptionChildren(parentOption) || [];
            const subChildrenToRemove = childrenToRemove.filter(
              (childOption) => expandedDict[`${getOptionValue(childOption)}`]
            );
            subChildrenToRemove.forEach((childOption) => {
              const otherToRemove = getChildrenToRemove(childOption);
              if (otherToRemove && otherToRemove.length) {
                childrenToRemove = childrenToRemove.concat(otherToRemove);
              }
            });
            return childrenToRemove;
          };

          const valuesToRemove = getChildrenToRemove(lastSelected).map((opt) =>
            getOptionValue(opt)
          );

          newOptions = newOptions.filter(
            (opt) => !valuesToRemove.includes(getOptionValue(opt))
          );

          updateExpandedDict({
            ...expandedDict,
            [`${optionValue}`]: false,
            ...valuesToRemove.reduce(
              (acc, valueToRemove) =>
                Object.assign(acc, {
                  [`${valueToRemove}`]: false,
                }),
              {}
            ),
          });
        } else {
          newOptions = [
            ...localOptions.slice(0, index),
            lastSelected,
            ...children.map((child) => ({
              ...child,
              _level: ((lastSelected as InternalOption)._level || 0) + 1,
            })),
            ...localOptions.slice(index + 1, localOptions.length),
          ];
          updateExpandedDict({
            ...expandedDict,
            [`${optionValue}`]: true,
          });
        }
        updateOptions(newOptions);
      } else {
        const currentSelectEl: any = selectEl.current;
        if (currentSelectEl && currentSelectEl.onMenuClose) {
          currentSelectEl.onMenuClose();
        }
        if (onChange) {
          onChange(selection, action);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [expandedDict, getOptionChildren, getOptionValue, internalOptions, onChange]
  );

  const customComponents = useMemo(
    () => ({
      ClearIndicator: TurboSelectClearIndicator,
      DropdownIndicator: TurboSelectDropdownIndicator,
      LoadingIndicator: TurboSelectLoadingIndicator,
      ...(components || {}),
      ...(isTree ? { Option: TurboSelectOption } : {}),
    }),
    [components, isTree]
  );

  const selectProps = {
    className: classNames(
      'TurboSelect',
      className,
      withBottomBorder && 'TurboSelect--onlyBottomBorder'
    ),
    classNamePrefix: 'TurboSelect',
    closeMenuOnSelect: !isTree,
    components: customComponents,
    expandedDict,
    getOptionChildren,
    getOptionLabel,
    getOptionValue,
    isClearable,
    isSearchable,
    isTree,
    loadingMessage,
    menuPlacement,
    noOptionsMessage,
    onChange: (isTree ? onChangeForTree : onChange) as any,
    openMenuOnClick,
    openMenuOnFocus,
    placeholder,
    ref: selectEl,
    value,
    ...otherProps,
  };
  if (loadOptions) {
    const AS = AsyncSelect as any;
    return (
      <AS
        cacheOptions
        defaultOptions
        loadOptions={loadOptions}
        {...selectProps}
      />
    );
  }
  const S = Select as any;
  return <S {...selectProps} options={internalOptions} />;
}

export interface TurboSingleSelectProps<OptionType>
  extends Omit<TurboSelectProps<OptionType, false>, 'onChange'> {
  onChange?: (
    value: OptionType | null | undefined,
    action: TurboSelectActionMeta<OptionType>
  ) => void;
}

export function TurboSingleSelect<OptionType = DefaultOptionType>(
  props: TurboSingleSelectProps<OptionType>
) {
  const { onChange } = props;
  return (
    <TurboSelect<OptionType>
      {...props}
      onChange={onChange as any}
      isMulti={false}
    />
  );
}

export interface TurboMultiSelectProps<OptionType>
  extends Omit<TurboSelectProps<OptionType, true>, 'onChange'> {
  onChange?: (
    value: OptionType[] | null | undefined,
    action: TurboSelectActionMeta<OptionType>
  ) => void;
}

export function TurboMultiSelect<OptionType = DefaultOptionType>(
  props: TurboMultiSelectProps<OptionType>
) {
  const { onChange } = props;
  return (
    <TurboSelect<OptionType, true>
      {...props}
      onChange={onChange as any}
      isMulti
    />
  );
}

export default TurboSelect;
